import { Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { parseAsArrayOf, parseAsString, parseAsStringEnum } from "nuqs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { GqlPlannerV2Visit, GqlPlannerV2VisitSimplifiedStatus, GqlPlannerVisitStatus, GqlProjectStatus } from "~/__generated__";
import GaiaDataList from "~/modules/app/components/GaiaDataList";
import GaiaPageGridComponent from "~/modules/app/components/GaiaPageGridComponent";
import GaiaPageLayoutColumn from "~/modules/app/components/GaiaPageLayoutColumn";
import GaiaTableLinkCell from "~/modules/app/components/GaiaTableLinkCell";
import ErrorScreen from "~/modules/app/screens/ErrorScreen";
import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { formatDateTime } from "~/modules/app/util/formatters";
import PlannerAreaSelect from "~/shared/components/PlannerAreaSelect";
import PlannerVisitSimplifiedStatusSelect from "~/shared/components/PlannerVisitSimplifiedStatusSelect";
import ProjectSelect from "~/shared/components/ProjectSelect";
import ProjectStatusSelect from "~/shared/components/ProjectStatusSelect";
import { useGridQueryState } from "~/shared/hooks/useGridQueryState";

import VisitSimplifiedStatusChip from "../../../../shared/components/VisitSimplifiedStatusChip";
import { useClientVisitListScreen_GetAll } from "../../api/useClientVisitListScreen_GetAll";
import useRequireCurrentClient from "../../hooks/useRequireCurrentClient";

export function ClientVisitListClientScreen() {
    const { t } = useTranslation("planner");

    const { client } = useRequireCurrentClient();

    const [queryState, setQueryState, gridModelProps] = useGridQueryState({
        projectIds: parseAsArrayOf(parseAsString).withDefault([]),
        projectStatus: parseAsArrayOf(parseAsStringEnum<GqlProjectStatus>(Object.values(GqlProjectStatus))).withDefault([]),
        plannerAreaIds: parseAsArrayOf(parseAsString).withDefault([]),
        plannerVisitSimplifiedStatus: parseAsStringEnum<GqlPlannerV2VisitSimplifiedStatus>(
            Object.values(GqlPlannerV2VisitSimplifiedStatus),
        ),
    });

    const params = useMemo(
        () => ({
            projectIds: queryState.projectIds.length > 0 ? queryState.projectIds : undefined,
            projectStatus: queryState.projectStatus.length > 0 ? queryState.projectStatus : undefined,
            plannerAreaIds: queryState.plannerAreaIds.length > 0 ? queryState.plannerAreaIds : undefined,
            plannerVisitSimplifiedStatus: queryState.plannerVisitSimplifiedStatus ?? undefined,
            page: queryState.page,
            pageSize: queryState.pageSize,
            sortBy: queryState.sortBy,
            sortOrder: queryState.sortOrder,
        }),
        [queryState],
    );

    const { data, loading, error, total } = useClientVisitListScreen_GetAll({
        clientId: client.id,
        params,
    });

    const handleParameterChange = <T,>(key: string, value: T) => {
        setQueryState({ [key]: value, page: 0 } as Parameters<typeof setQueryState>[0]);
    };

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: "plannerArea.name",
                headerName: t("visitListScreen.areaName"),
                valueGetter: (_, row) => row.plannerArea?.name,
                flex: 1,
            },
            {
                field: "protocol.name",
                headerName: t("visitListScreen.protocol"),

                renderCell: ({ row }) => (
                    <Stack>
                        <Typography variant="body1">{row.protocol?.name}</Typography>
                        <Typography variant="caption">
                            {row.protocolVisit?.name ?? t("visitListScreen.visitName")} {row.protocolVisit?.number ?? ""}
                        </Typography>
                    </Stack>
                ),

                flex: 1,
            },
            {
                field: "requiredFieldworkers",
                headerName: t("visitListScreen.requiredWorkers"),
                valueGetter: (_, row) => row.requiredFieldworkers,
                flex: 1,
            },
            {
                field: "startTime",
                headerName: t("visitListScreen.startTime"),
                valueGetter: (_, row) =>
                    row.status !== GqlPlannerVisitStatus.Unplanned && row.startTime !== null
                        ? formatDateTime(parseStringToDate(row.startTime))
                        : null,
                flex: 1,
            },
            {
                field: "status",
                headerName: t("visitListScreen.status"),
                renderCell: ({ row }: GridRenderCellParams<GqlPlannerV2Visit>) => (
                    <VisitSimplifiedStatusChip visitStatus={row.simplifiedStatus} />
                ),
                flex: 1,
            },
            {
                type: "actions",
                field: "",
                maxWidth: 40,
                valueGetter: (_, visit) => {
                    if (visit.status !== GqlPlannerVisitStatus.Approved) return;

                    const clientSlug = visit.plannerArea.project.client.slug;
                    const projectSlug = visit.plannerArea.project.slug;

                    return `/c/${clientSlug}/p/${projectSlug}/visits/${visit.id}`;
                },
                renderCell: ({ value }) => (value !== undefined ? <GaiaTableLinkCell value={value} /> : undefined),
            },
        ],
        [t],
    );

    if (!data && !loading && error) {
        return <ErrorScreen error={error} />;
    }

    return (
        <GaiaPageLayoutColumn maxWidth="xl">
            <GaiaPageGridComponent title={t("visitListScreen.title")}>
                <Grid container spacing={2} sx={{ alignItems: "flex-start", "& .MuiAutocomplete-root": { width: "100%" } }}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <ProjectSelect
                            label={t("visitListScreen.filterProjectName")}
                            size="small"
                            multiple
                            value={queryState.projectIds}
                            clientIds={[client.id]}
                            onChange={(value: string[]) => handleParameterChange("projectIds", value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <ProjectStatusSelect
                            value={queryState.projectStatus}
                            onChange={value => handleParameterChange("projectStatus", value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <PlannerAreaSelect
                            label={t("visitListScreen.filterAreaName")}
                            value={queryState.plannerAreaIds}
                            clientIds={[client.id]}
                            projectIds={queryState.projectIds}
                            onChange={value => handleParameterChange("plannerAreaIds", value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <PlannerVisitSimplifiedStatusSelect
                            value={queryState.plannerVisitSimplifiedStatus}
                            onChange={value => handleParameterChange("plannerVisitSimplifiedStatus", value)}
                        />
                    </Grid>
                </Grid>
                <GaiaDataList
                    data={data || []}
                    loading={loading}
                    error={error}
                    totalRows={total || 0}
                    columns={columns}
                    gridModels={gridModelProps}
                />
            </GaiaPageGridComponent>
        </GaiaPageLayoutColumn>
    );
}
